import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAiAssistant } from "context/AiAssistantContext";
import { assistantService } from "services/assistantService";
import magicStarIcon from "assets/images/magic_star.svg";

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M2 9h14M10 3l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="ai-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="30" strokeDashoffset="10" strokeLinecap="round" />
  </svg>
);

function buildContextString(ctx) {
  if (!ctx) return "general";
  if (ctx.type === "report") return `report:${ctx.id || "unknown"}`;
  if (ctx.type === "news") return `news:${ctx.id || "unknown"}`;
  if (ctx.type === "material") return `material:${ctx.code || "unknown"}`;
  return ctx.type || "general";
}

function usePageContext() {
  const location = useLocation();
  return useMemo(() => {
    const p = location.pathname;
    const reportMatch = p.match(/\/reports\/view\/([^/]+)/);
    if (reportMatch) return { type: "report", id: reportMatch[1] };
    const newsMatch = p.match(/\/news\/news-view\/([^/]+)/);
    if (newsMatch) return { type: "news", id: newsMatch[1] };
    if (p.startsWith("/charts")) return { type: "charts" };
    if (p.startsWith("/materials")) {
      const code = p.split("/").filter(Boolean).pop();
      return { type: "material", code };
    }
    if (p.startsWith("/models")) return { type: "models" };
    if (p.startsWith("/dashboard")) return { type: "dashboard" };
    if (p.startsWith("/settings")) return { type: "settings" };
    return { type: "general" };
  }, [location.pathname]);
}

function ChatSection({ pageContext }) {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages((prev) => [...prev, { role: "user", content: text, id: Date.now() }]);
    setInput("");
    setLoading(true);

    try {
      const data = await assistantService.chat({
        message: text,
        context: buildContextString(pageContext),
        language: i18n.language || "en",
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply || "", id: Date.now() + 1 }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: t("aiAssistant.chat.error"), id: Date.now() + 1 }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, pageContext, i18n.language, t]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="ai-chat-section">
      <div className="ai-chat-messages">
        {messages.length === 0 && (
          <p className="ai-chat-empty">{t("aiAssistant.chat.empty")}</p>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`ai-chat-bubble ai-chat-bubble--${msg.role}`}>
            <p>{msg.content}</p>
          </div>
        ))}
        {loading && (
          <div className="ai-chat-bubble ai-chat-bubble--assistant ai-chat-bubble--loading">
            <SpinnerIcon />
            <span>{t("aiAssistant.chat.thinking")}</span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div className="ai-chat-input-row">
        <textarea
          className="ai-chat-textarea"
          rows={2}
          placeholder={t("aiAssistant.chat.placeholder")}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <button
          className="ai-chat-send-btn"
          onClick={handleSend}
          disabled={loading || !input.trim()}
          aria-label={t("aiAssistant.chat.send")}
        >
          {loading ? <SpinnerIcon /> : <SendIcon />}
        </button>
      </div>
    </div>
  );
}

function SummarizeSection({ pageContext }) {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSummarize = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const prompt = t("aiAssistant.summarize.prompt", {
        type: pageContext?.type || "content",
        title: pageContext?.title || pageContext?.id || "",
      });
      const data = await assistantService.chat({
        message: prompt,
        context: buildContextString(pageContext),
        language: i18n.language || "en",
      });
      setSummary(data.reply || "");
    } catch {
      setError(t("aiAssistant.chat.error"));
    } finally {
      setLoading(false);
    }
  }, [pageContext, i18n.language, t]);

  return (
    <div className="ai-summarize-section">
      <div className="ai-section-separator" />
      <div className="ai-section-header">
        <span className="ai-section-tag">{t("aiAssistant.summarize.tag")}</span>
      </div>
      {!summary && !loading && (
        <button className="ai-summarize-btn" onClick={handleSummarize} disabled={loading}>
          {t("aiAssistant.summarize.button")}
        </button>
      )}
      {loading && (
        <div className="ai-summarize-loading">
          <SpinnerIcon />
          <span>{t("aiAssistant.chat.thinking")}</span>
        </div>
      )}
      {error && <p className="ai-summarize-error">{error}</p>}
      {summary && (
        <div className="ai-summary-block">
          <h3 className="ai-summary-title">{t("aiAssistant.summarize.title")}</h3>
          <p className="ai-summary-body">{summary}</p>
          <button
            className="ai-summarize-btn ai-summarize-btn--refresh"
            onClick={() => { setSummary(""); handleSummarize(); }}
          >
            {t("aiAssistant.summarize.refresh")}
          </button>
        </div>
      )}
    </div>
  );
}

const REPORT_DETAIL_PILLS = ["short", "detailed", "original"];
const WRITING_STYLE_PILLS = ["analytical", "management", "original"];

function CustomizeSection({ pageContext }) {
  const { t } = useTranslation();
  const [reportDetail, setReportDetail] = useState("original");
  const [writingStyle, setWritingStyle] = useState("original");

  if (pageContext?.type !== "report") return null;

  return (
    <div className="ai-customize-section">
      <div className="ai-section-separator" />
      <h4 className="ai-customize-title">{t("aiAssistant.customize.title")}</h4>
      <div className="ai-customize-group">
        <span className="ai-customize-label">{t("aiAssistant.customize.reportDetails")}</span>
        <div className="ai-pill-group">
          {REPORT_DETAIL_PILLS.map((pill) => (
            <button
              key={pill}
              className={`ai-pill ${reportDetail === pill ? "ai-pill--active" : ""}`}
              onClick={() => setReportDetail(pill)}
            >
              {t(`aiAssistant.customize.pills.${pill}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="ai-customize-group">
        <span className="ai-customize-label">{t("aiAssistant.customize.writingStyle")}</span>
        <div className="ai-pill-group">
          {WRITING_STYLE_PILLS.map((pill) => (
            <button
              key={pill}
              className={`ai-pill ${writingStyle === pill ? "ai-pill--active" : ""}`}
              onClick={() => setWritingStyle(pill)}
            >
              {t(`aiAssistant.customize.pills.${pill}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AiAssistantPanel() {
  const { t } = useTranslation();
  const { isOpen, close } = useAiAssistant();
  const [activeTab, setActiveTab] = useState("chat");
  const pageContext = usePageContext();

  const showSummarize = ["report", "news", "material"].includes(pageContext?.type);

  return (
    <aside
      className={`ai-assistant-panel ${isOpen ? "ai-assistant-panel--open" : ""}`}
      aria-label={t("aiAssistant.title")}
      role="complementary"
    >
      <div className="ai-panel-header">
        <div className="ai-panel-header-left">
          <img src={magicStarIcon} alt="" className="ai-panel-header-icon" />
          <span className="ai-panel-title">{t("aiAssistant.title")}</span>
        </div>
        <button className="ai-panel-close-btn" onClick={close} aria-label={t("aiAssistant.close")}>
          <CloseIcon />
        </button>
      </div>

      <div className="ai-panel-tabs">
        <button
          className={`ai-tab ${activeTab === "chat" ? "ai-tab--active" : ""}`}
          onClick={() => setActiveTab("chat")}
        >
          {t("aiAssistant.tabs.chat")}
        </button>
        {showSummarize && (
          <button
            className={`ai-tab ${activeTab === "summarize" ? "ai-tab--active" : ""}`}
            onClick={() => setActiveTab("summarize")}
          >
            {t("aiAssistant.tabs.summarize")}
          </button>
        )}
      </div>

      <div className="ai-panel-body">
        {activeTab === "chat" && <ChatSection pageContext={pageContext} />}
        {activeTab === "summarize" && showSummarize && (
          <>
            <SummarizeSection pageContext={pageContext} />
            <CustomizeSection pageContext={pageContext} />
          </>
        )}
      </div>
    </aside>
  );
}
